import {
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
  ComponentType,
} from "react";
import { cn } from "../../lib/utils";
import { Button } from "../../ui/button";
import { ScrollArea } from "../../ui/scroll-area";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  PanelLeft,
  Search,
  X,
} from "lucide-react";
import { MDXProvider } from "@mdx-js/react";
import { SiteNavbar, type SiteNavbarProps } from "../SiteNavbar";
import { ThemeToggle } from "../ThemeToggle";
import { DocsSearch } from "./DocsSearch";
import { baseMdxComponents } from "./mdx-components";
import type { DocSection, DocGroup } from "../../types";

export interface DocsLayoutProps {
  docGroups: DocGroup[];
  docSections: DocSection[];
  activeSectionId: string;
  onSectionChange: (id: string) => void;
  logo: ReactNode;
  productName: string;
  siteBaseUrl?: string;
  extraMdxComponents?: Record<string, ComponentType>;
  navbarProps?: Partial<SiteNavbarProps>;
  showAIActions?: boolean;
  showCopyMarkdown?: boolean;
}

function DocsSidebar({
  docGroups,
  activeSection,
  onSectionChange,
  isCollapsed,
}: {
  docGroups: DocGroup[];
  activeSection: string;
  onSectionChange: (id: string) => void;
  isCollapsed: boolean;
}) {
  return (
    <aside
      className={cn(
        "hidden lg:block shrink-0 transition-all duration-300 ease-in-out overflow-x-clip",
        isCollapsed ? "w-0 opacity-0" : "w-56 opacity-100",
      )}
    >
      <div className="sticky top-[69px] w-56">
        <ScrollArea className="h-[calc(100vh-69px)] pr-4">
          <nav className="pt-16 pb-6">
            {docGroups.map((group, groupIdx) => (
              <div key={group.label} className={groupIdx > 0 ? "mt-6" : ""}>
                <h4 className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                  {group.label}
                </h4>
                <ul className="space-y-0.5">
                  {group.sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => onSectionChange(section.id)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all duration-150",
                          activeSection === section.id
                            ? "bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                            : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100",
                        )}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </ScrollArea>
      </div>
    </aside>
  );
}

function DocsRightSidebar({
  section,
  activeHeadingId,
  siteBaseUrl,
  productName,
  showAIActions = true,
  showCopyMarkdown = true,
}: {
  section: DocSection;
  activeHeadingId: string | null;
  siteBaseUrl?: string;
  productName: string;
  showAIActions?: boolean;
  showCopyMarkdown?: boolean;
}) {
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const tocNavRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!activeHeadingId || !tocNavRef.current) return;
    const activeButton = tocNavRef.current.querySelector(
      `[data-heading-id="${activeHeadingId}"]`,
    );
    if (activeButton) {
      activeButton.scrollIntoView({ block: "nearest" });
    }
  }, [activeHeadingId]);

  const handleCopyMarkdown = async () => {
    await navigator.clipboard.writeText(section.rawContent);
    setCopiedMarkdown(true);
    setTimeout(() => setCopiedMarkdown(false), 2000);
  };

  const handleScrollToHeading = (headingId: string) => {
    const element = document.getElementById(headingId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openInChatGPT = () => {
    const baseUrl = siteBaseUrl || "";
    const message = `Read ${baseUrl}/llms-full.txt - I'm on the ${section.title} page and have questions about ${productName}.`;
    const url = `https://chatgpt.com/?hints=search&q=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const openInClaude = () => {
    const baseUrl = siteBaseUrl || "";
    const message = `Read ${baseUrl}/llms-full.txt - I'm on the ${section.title} page and have questions about ${productName}.`;
    const url = `https://claude.ai/new?q=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  return (
    <aside className="hidden xl:block w-52 shrink-0">
      <div className="sticky top-[133px] max-h-[calc(100vh-133px)] flex flex-col pb-6">
        {section.headings.length > 0 && (
          <div className="min-h-0 flex-1 flex flex-col pb-4">
            <h4 className="text-[13px] font-medium text-zinc-900 dark:text-zinc-100 mb-3 shrink-0">
              On this page
            </h4>
            <nav
              ref={tocNavRef}
              className="flex-1 min-h-0 overflow-y-auto toc-scroll"
            >
              <ul className="space-y-2">
                {section.headings.map((heading) => (
                  <li key={heading.id}>
                    <button
                      data-heading-id={heading.id}
                      onClick={() => handleScrollToHeading(heading.id)}
                      className={cn(
                        "text-left text-[13px] leading-normal transition-colors w-full cursor-pointer",
                        heading.level === 3 && "pl-3",
                        activeHeadingId === heading.id
                          ? "text-zinc-900 dark:text-zinc-100"
                          : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300",
                      )}
                    >
                      {heading.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}

        {section.headings.length > 0 && (
          <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 shrink-0" />
        )}

        <div className="space-y-2">
          {showCopyMarkdown && (
            <button
              onClick={handleCopyMarkdown}
              className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
            >
              {copiedMarkdown ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              <span>{copiedMarkdown ? "Copied!" : "Copy Markdown"}</span>
            </button>
          )}

          {showAIActions && (
            <>
              <button
                onClick={openInChatGPT}
                className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.896zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08-4.778 2.758a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
                </svg>
                <span>Open in ChatGPT</span>
              </button>

              <button
                onClick={openInClaude}
                className="flex items-center gap-2.5 text-[13px] text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors w-full py-0.5 cursor-pointer"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 1200 1200"
                  fill="currentColor"
                >
                  <path d="M234 800l235-132 4-11-4-7h-11l-39-2-134-4-117-5-112-6-29-6-26-35 3-17 23-16 34 3 76 5 113 8 82 5 122 12h19l3-8-7-4-5-5-117-79-127-84-66-48-36-25-18-23-8-50 33-36 44 3 11 3 44 34 95 73 124 91 18 15 7-5 1-4-8-13-68-122-72-124-31-51-9-31c-3-13-5-23-5-36l37-50 21-7 49 7 21 18 31 70 50 111 77 150 23 45 12 41 5 13h8v-7l6-85 12-104 11-134 4-38 19-45 37-24 29 14 24 34-3 22-14 92-28 145-18 96h11l12-12 49-65 82-103 36-40 42-45 27-22h52l37 57-17 58-52 67-44 57-63 84-39 68 4 5 9-1 142-30 77-14 91-16 42 19 4 20-16 40-98 24-115 23-170 40-3 2 3 3 77 7 33 2h81l150 11 39 26 24 32-4 24-61 31-81-19-190-46-65-16h-9v6l20 32 117 175 6 54-8 17-31 11-33-6-68-96-71-108-56-96-7 4-34 361-15 18-37 14-30-23-16-37 16-74 19-95 16-76 14-95 8-31v-2l-7 1-71 98-109 146-85 92-21 8-35-18 3-37 20-29 119-151 71-93 47-54v-8h-3L205 929l-56 8-24-23 3-37 11-12 95-65z" />
                </svg>
                <span>Open in Claude</span>
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}

export function DocsLayout({
  docGroups,
  docSections,
  activeSectionId,
  onSectionChange,
  logo,
  productName,
  siteBaseUrl,
  extraMdxComponents,
  navbarProps,
  showAIActions = true,
  showCopyMarkdown = true,
}: DocsLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentSection = docSections.find((s) => s.id === activeSectionId);
  const currentIndex = docSections.findIndex((s) => s.id === activeSectionId);
  const prevSection = currentIndex > 0 ? docSections[currentIndex - 1] : null;
  const nextSection =
    currentIndex < docSections.length - 1
      ? docSections[currentIndex + 1]
      : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSearchNavigate = useCallback(
    (sectionId: string, headingId?: string) => {
      if (headingId && sectionId === activeSectionId) {
        const el = document.getElementById(headingId);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        onSectionChange(sectionId);
        if (headingId) {
          setTimeout(() => {
            const el = document.getElementById(headingId);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }
      }
    },
    [activeSectionId, onSectionChange],
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setActiveHeadingId(null);
  }, [activeSectionId]);

  const handleScroll = useCallback(() => {
    if (!currentSection || currentSection.headings.length === 0) return;

    const headingElements = currentSection.headings
      .map((h) => ({
        id: h.id,
        element: document.getElementById(h.id),
      }))
      .filter((h) => h.element !== null);

    if (headingElements.length === 0) return;

    const scrollTop = window.scrollY;
    const offset = 100;

    let currentHeading = headingElements[0].id;

    for (const { id, element } of headingElements) {
      if (element && element.offsetTop - offset <= scrollTop) {
        currentHeading = id;
      }
    }

    setActiveHeadingId(currentHeading);
  }, [currentSection]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const mdxComponents = {
    ...baseMdxComponents,
    ...extraMdxComponents,
  };

  const docsRightContent = (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => setSearchOpen(true)}
        className="hidden sm:flex items-center gap-2 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800"
      >
        <Search className="h-4 w-4" />
        <span className="text-sm">Search Documentation</span>
        <kbd className="hidden md:inline-flex ml-2 pointer-events-none h-5 select-none items-center gap-1 rounded border border-zinc-200 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 px-1.5 font-mono text-[10px] font-medium text-zinc-600 dark:text-zinc-400">
          <span className="text-xs">{"\u2318"}</span>K
        </kbd>
      </Button>
      <ThemeToggle />
    </div>
  );

  const mobileSidebarOverlay = mobileMenuOpen && (
    <div
      className="fixed inset-0 bg-black/50 z-40 lg:hidden"
      onClick={() => setMobileMenuOpen(false)}
    />
  );

  const mobileSidebar = (
    <aside
      className={cn(
        "fixed top-0 left-0 z-50 h-screen w-64 bg-white dark:bg-zinc-950 border-r border-zinc-200 dark:border-zinc-800 transform transition-transform lg:hidden",
        mobileMenuOpen ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="flex items-center justify-between px-4 py-4 border-b border-zinc-200 dark:border-zinc-800">
        <a href="/">{logo}</a>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
      <ScrollArea className="h-[calc(100vh-65px)]">
        <nav className="p-3">
          {docGroups.map((group, groupIdx) => (
            <div key={group.label} className={groupIdx > 0 ? "mt-5" : ""}>
              <h4 className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                {group.label}
              </h4>
              <ul className="space-y-0.5">
                {group.sections.map((section) => (
                  <li key={section.id}>
                    <button
                      onClick={() => {
                        onSectionChange(section.id);
                        setMobileMenuOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-[13px] transition-all duration-150",
                        activeSectionId === section.id
                          ? "bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 font-medium"
                          : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800/50 hover:text-zinc-900 dark:hover:text-zinc-100",
                      )}
                    >
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );

  const mobileMenuButton = (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setMobileMenuOpen(true)}
      className="lg:hidden text-zinc-600 dark:text-zinc-400"
    >
      <PanelLeft className="h-5 w-5" />
    </Button>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      {mobileSidebarOverlay}
      {mobileSidebar}

      <DocsSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onNavigate={handleSearchNavigate}
        docSections={docSections}
      />

      <SiteNavbar
        logo={logo}
        variant="standard"
        rightContent={docsRightContent}
        sidebarToggle={mobileMenuButton}
        {...(navbarProps || {})}
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="flex gap-8">
          <DocsSidebar
            docGroups={docGroups}
            activeSection={activeSectionId}
            onSectionChange={onSectionChange}
            isCollapsed={sidebarCollapsed}
          />

          <main className="flex-1 min-w-0 transition-all duration-300 ease-in-out">
            <div className="hidden lg:block pt-4 pb-4 -ml-1">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="p-1.5 rounded-md text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
              >
                <PanelLeft className="h-5 w-5" />
              </button>
            </div>

            <div className="flex gap-8">
              <div ref={contentRef} className="flex-1 min-w-0 pb-8">
                <div className="mdx-content">
                  <MDXProvider components={mdxComponents}>
                    {currentSection && <currentSection.Component />}
                  </MDXProvider>
                </div>

                <div className="mt-16 pt-8 border-t border-zinc-200 dark:border-zinc-800 grid grid-cols-2 gap-3">
                  {prevSection ? (
                    <button
                      onClick={() => onSectionChange(prevSection.id)}
                      className="group flex flex-col items-start text-left p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-all"
                    >
                      <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500 mb-0.5 flex items-center gap-0.5 uppercase tracking-wide">
                        <ChevronLeft className="h-3 w-3" />
                        Previous
                      </span>
                      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">
                        {prevSection.title}
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}
                  {nextSection ? (
                    <button
                      onClick={() => onSectionChange(nextSection.id)}
                      className="group flex flex-col items-end text-right p-3 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50 transition-all"
                    >
                      <span className="text-[10px] font-medium text-zinc-400 dark:text-zinc-500 mb-0.5 flex items-center gap-0.5 uppercase tracking-wide">
                        Next
                        <ChevronRight className="h-3 w-3" />
                      </span>
                      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">
                        {nextSection.title}
                      </span>
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              </div>

              {currentSection && (
                <DocsRightSidebar
                  key={currentSection.id}
                  section={currentSection}
                  activeHeadingId={activeHeadingId}
                  siteBaseUrl={siteBaseUrl}
                  productName={productName}
                  showAIActions={showAIActions}
                  showCopyMarkdown={showCopyMarkdown}
                />
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
